import angular from "angular";

import { Accounts, Ajax, Avatar, AvatarPictureType, CompanyAccount, WorkflowEvent } from "@salaxy/core";

import { GravatarHelpers, SessionService, UiHelpers, UploadService } from "../../services";

/** Company account controller for editing basic information. */
export class CompanyAccountController implements angular.IComponentController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Accounts", "SessionService", "AjaxNg1", "UiHelpers", "UploadService"];

  /** Current company account */
  public current: CompanyAccount = null;

  /** Constructor for dependency injection */
  constructor(
    private accounts: Accounts,
    private sessionService: SessionService,
    private ajax: Ajax,
    private uiHelpers: UiHelpers,
    private uploadService: UploadService,
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    if (!this.current) {
      this.accounts.getCompany().then((company) => {
        this.current = company;
      });
    }
  }

  /** Gets the language of the current company */
  public getAccountLanguage() {
    return this.current?.language;
  }
  /** Updates the company */
  public saveCurrent(): Promise<CompanyAccount> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    return this.ajax.postJSON("/accounts/company", this.current).then((company: CompanyAccount) => {
      this.current = company;
      return this.sessionService.checkSession().then(() => {
        loading.dismiss();
        return company;
      });
    });
  }

  /**
   * Uploads avatar image file to the server.
   * @param avatar - selected file
   */
  public uploadAvatarImage(avatar: any, current: CompanyAccount = null) {
    if (!avatar) {
      return;
    }
    current = current ?? this.current;
    this.uploadService.upload<Avatar>(this.accounts.getAvatarUploadUrl(), { avatar })
      .then((resp) => {
        current.avatar.url = resp.url;
        current.avatar.pictureType = resp.pictureType;
      });
  }

  /** Called when avatar changes */
  public emailForAvatarChanged(current: CompanyAccount = null) {
    current = current  ?? this.current;
    if (!current || !current.avatar) {
      return;
    }
    if (current.avatar.pictureType === AvatarPictureType.Gravatar) {
      current.avatar.url = GravatarHelpers.getGravatarUrl(current.contact.email);
    }
  }
  /** Called when avatar changes */
  public typeForAvatarChanged(current: CompanyAccount = null) {
    current = current  ?? this.current;
    if (!current || !current.avatar) {
      return;
    }
    if (current.avatar.pictureType === AvatarPictureType.Gravatar) {
      current.avatar.url = GravatarHelpers.getGravatarUrl(current.contact.email);
    } else {
      if (GravatarHelpers.isGravatarUrl(current.avatar.url)) {
        current.avatar.url = null;
      }
      if (current.avatar.pictureType === AvatarPictureType.Uploaded) {
        const originalAvatar = (this.sessionService.getPersonAccount() || {}).avatar;
        if (!current.avatar.url && originalAvatar && originalAvatar.url) {
          if (originalAvatar.pictureType === AvatarPictureType.Uploaded &&
            !GravatarHelpers.isGravatarUrl(originalAvatar.url)) {
            current.avatar.url = originalAvatar.url;
          }
        }
      }
    }
  }

  /**
   * Adds/updates the workflow event for the current using API.
   * @param wfEvent - Workflow event to add/update.
   * @returns - Reloaded item.
   */
  public saveWorkflowEvent(wfEvent: WorkflowEvent): Promise<void | CompanyAccount> {
    return this.accounts.saveWorkflowEvent(this.current, wfEvent).then(() => {
      return this.accounts.getCompany().then((company) => {
        this.current = company;
      });
    });
  }

  /**
   * Deletes the given event or all events with given type using API.
   * @param wfIdOrType Id or type of the workflow event.
   * @returns - Reloaded item.
   */
  public deleteWorkflowEvent(wfIdOrType: string): Promise<void | CompanyAccount> {
    return this.accounts.deleteWorkflowEvent(this.current, wfIdOrType).then(() => {
      return this.accounts.getCompany().then((company) => {
        this.current = company;
      });
    });
  }

  /**
   * Gets a single workflow event for the item.
   * @param wfId Id the workflow event.
   * @returns - Workflow event.
   */
  public getWorkflowEvent(wfId: string): Promise<WorkflowEvent> {
    return this.accounts.getWorkflowEvent(this.current, wfId);
  }

  /**
   * Opens modal for avatar edit
   */
  public openEditDialog(editType: "avatar" | "contactInfo"): void {
    const logic = {
      typeForAvatarChanged: (current) => this.typeForAvatarChanged(current),
      uploadAvatarImage: (file, current) => this.uploadAvatarImage(file, current),
      emailForAvatarChanged: (current) => this.emailForAvatarChanged(current)
    };
    this.uiHelpers.openEditDialog(
      editType === "avatar" ? "salaxy-components/modals/account/AccountAvatarEdit.html" : "salaxy-components/modals/account/AccountContactEdit.html",
      this.current,
      logic
      ).then((result) => {
        if (result.action === "ok") {
          this.current = result.item;
          this.saveCurrent();
        }
      });
  }
}
